// Cookie-consent state store.
//
// Single source of truth for whether the visitor has agreed to non-essential
// (analytics) cookies. GDPR/PECR require *prior* consent, so nothing that sets
// a non-essential cookie may run until getConsent() returns 'accepted'.
//
// The store is a tiny pub/sub around localStorage so any component (the cookie
// banner that sets the choice, the analytics loader that reacts to it) stays in
// sync without prop-drilling or a context provider.

export type ConsentValue = 'accepted' | 'declined';

// Kept identical to the key the original CookieConsent banner used so returning
// visitors keep their previous choice.
const STORAGE_KEY = 'wl_cookie_consent';

const listeners = new Set<(value: ConsentValue | null) => void>();

// Returns the stored choice, or null if the visitor has not decided yet.
export function getConsent(): ConsentValue | null {
  if (typeof window === 'undefined') return null;
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === 'accepted' || value === 'declined' ? value : null;
}

// Persists the visitor's choice and notifies subscribers so analytics can start
// (on 'accepted') or stay off (on 'declined') immediately, without a reload.
export function setConsent(value: ConsentValue): void {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, value);
  }
  listeners.forEach((listener) => listener(value));
}

// Subscribe to consent changes. Returns an unsubscribe function.
export function subscribeConsent(
  listener: (value: ConsentValue | null) => void
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
